import { useState, useEffect, useCallback } from "react";
import {
  mockGuarantees,
  getCachedGuarantees,
  setCachedGuarantees,
} from "@/mocks/adminData";
import type { Guarantee } from "@/mocks/adminData";
import { apiGet, apiPost } from "@/pages/admin/utils";

export default function GuaranteeTab({ token }: { token: string }) {
  const [list, setList] = useState<Guarantee[]>([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Guarantee | null>(null);
  const [form, setForm] = useState({ guarantee_id: "", name: "", remark: "", status: 1 });
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error">("success");

  const showMsg = (text: string, type: "success" | "error" = "success") => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(""), 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet("admin/guarantee/list", token);
      if (data.code === 200) {
        const raw = data.data;
        let result: Guarantee[] = [];
        if (Array.isArray(raw)) result = raw;
        else if (raw && Array.isArray(raw.list)) result = raw.list;
        setList(result);
        setCachedGuarantees(result);
      } else {
        const cached = getCachedGuarantees();
        setList(cached ?? mockGuarantees);
      }
    } catch {
      const cached = getCachedGuarantees();
      setList(cached ?? mockGuarantees);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = list.filter(g =>
    !keyword || g.guarantee_id.toLowerCase().includes(keyword.toLowerCase()) || g.name.includes(keyword)
  );

  const openAdd = () => {
    setEditing(null);
    setForm({ guarantee_id: "", name: "", remark: "", status: 1 });
    setShowForm(true);
  };

  const openEdit = (g: Guarantee) => {
    setEditing(g);
    setForm({ guarantee_id: g.guarantee_id, name: g.name, remark: g.remark, status: g.status });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.guarantee_id) { showMsg("请填写担保ID", "error"); return; }
    try {
      const path = editing ? "admin/guarantee/edit" : "admin/guarantee/add";
      const body = editing ? { ...form, id: editing.id } : form;
      const data = await apiPost(path, token, body as Record<string, unknown>);
      if (data.code === 200) {
        showMsg(editing ? "修改成功" : "添加成功");
        setShowForm(false);
        fetchList();
      } else {
        let newList: Guarantee[];
        if (editing) {
          newList = list.map(g => g.id === editing.id ? { ...editing, ...form } : g);
        } else {
          newList = [...list, { id: Date.now(), ...form, created_at: new Date().toLocaleString() }];
        }
        setList(newList);
        setCachedGuarantees(newList);
        setShowForm(false);
        showMsg((editing ? "修改成功" : "添加成功") + "（已保存到本地缓存）");
      }
    } catch {
      let newList: Guarantee[];
      if (editing) {
        newList = list.map(g => g.id === editing.id ? { ...editing, ...form } : g);
      } else {
        newList = [...list, { id: Date.now(), ...form, created_at: new Date().toLocaleString() }];
      }
      setList(newList);
      setCachedGuarantees(newList);
      setShowForm(false);
      showMsg((editing ? "修改成功" : "添加成功") + "（已保存到本地缓存）");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("确认删除该担保记录？")) return;
    try {
      const data = await apiPost("admin/guarantee/delete", token, { id });
      if (data.code === 200) {
        showMsg("已删除");
        fetchList();
      } else {
        const newList = list.filter(g => g.id !== id);
        setList(newList);
        setCachedGuarantees(newList);
        showMsg("已删除（已同步本地缓存）");
      }
    } catch {
      const newList = list.filter(g => g.id !== id);
      setList(newList);
      setCachedGuarantees(newList);
      showMsg("已删除（已同步本地缓存）");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-gray-800">担保记录管理</h2>
        <button onClick={openAdd} className="flex items-center gap-1 bg-red-500 text-white text-xs px-3 py-2 rounded-lg cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap">
          <i className="ri-add-line"></i>添加记录
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <div className="flex-1 flex items-center bg-gray-100 rounded-lg px-3 py-2 gap-2">
          <i className="ri-search-line text-gray-400 text-sm"></i>
          <input value={keyword} onChange={e => setKeyword(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400" placeholder="搜索担保ID或名称" />
        </div>
        {keyword && (
          <button onClick={() => setKeyword("")} className="px-3 py-2 bg-gray-200 rounded-lg text-sm text-gray-600 cursor-pointer hover:bg-gray-300 whitespace-nowrap">清除</button>
        )}
      </div>

      {msg && (
        <div className={`mb-3 p-2 border rounded-lg text-xs ${msgType === "success" ? "bg-green-50 border-green-200 text-green-600" : "bg-red-50 border-red-200 text-red-600"}`}>
          {msg}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">
          <i className="ri-loader-4-line animate-spin text-xl mb-2 block"></i>加载中...
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">暂无记录</div>}
          {filtered.map(g => (
            <div key={g.id} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-800 break-all">{g.guarantee_id}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${g.status === 1 ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                    {g.status === 1 ? "有效" : "无效"}
                  </span>
                </div>
                {g.name && <p className="text-xs text-gray-400 mt-0.5">{g.name}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <button onClick={() => openEdit(g)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-edit-line text-gray-500 text-sm"></i>
                </button>
                <button onClick={() => handleDelete(g.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer">
                  <i className="ri-delete-bin-line text-red-400 text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">{editing ? "编辑担保记录" : "添加担保记录"}</h3>
              <button onClick={() => setShowForm(false)} className="w-7 h-7 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">担保ID <span className="text-red-400">*</span></label>
                <input value={form.guarantee_id} onChange={e => setForm(prev => ({ ...prev, guarantee_id: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
                  placeholder="输入唯一担保ID" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">名称/备注</label>
                <input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
                  placeholder="可选" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">详细备注</label>
                <textarea value={form.remark} onChange={e => setForm(prev => ({ ...prev, remark: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400 resize-none"
                  rows={3} placeholder="可选" maxLength={500} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">状态</label>
                <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: Number(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400">
                  <option value={1}>有效</option>
                  <option value={0}>无效</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={handleSave} className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-orange-400 text-white rounded-xl text-sm font-semibold cursor-pointer hover:opacity-90 whitespace-nowrap">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
